import React, { useState, useEffect, useRef, useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useFormik } from 'formik';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import { CompetitionService } from '../../service/CompetitionService';

import CompetitionUtils from '../../utilities/CompetitionUtils';
import useAuth from '../../hooks/useAuth';
import EnumService from '../../service/EnumService';
import { AutoComplete } from 'primereact/autocomplete';
import { Checkbox } from 'primereact/checkbox';
import GeneralUtils from '../../utilities/GeneralUtils';
import { Calendar } from 'primereact/calendar';

let emptyCompetition = {
    id: null,
    name: '',
    positionCategory: '',
    type: 'JOB_DEBUTANT',
    startDate: '',
    submitEndDate: '',
    status: 'DRAFT',
    clarificationInterval: null,
    contestationInterval: null,
    folderId: null,
    institution: null,
    legalBasis: null,
    hasResponsibility: true,
    anfpRequest: false,
    contactName: '',
    contactPosition: '',
    contactPhone: '',
    contactEmail: '',
    responsibleName: '',
    responsiblePosition: '',
    responsiblePhone: '',
    responsibleEmail: '',
    managerName: '',
    managerPosition: '',
    institutionRegistrationNumber: '',
    institutionRegistrationDate: '',
};

const CompetitionJobEditDialog = (props) => {
    const [competition, setCompetition] = useState(emptyCompetition);
    const [isNew, setIsNew] = useState(false);
    // const [statuses, setStatuses] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [positionCategories, setPositionCategories] = useState([]);
    const [legalBasisList, setLegalBasesList] = useState([]);
    const [filteredEntityLookups, setFilteredEntityLookups] = useState([]);

    const toast = useRef(null);
    const { auth } = useAuth();
    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        let _competition = null

        const getPositionCategories = async () => {
            setPositionCategories(await EnumService.getEnumByName('PositionCategory', 'job'));
        };

        if (props.value) {
            setIsNew(false);
            _competition = GeneralUtils.emptyInNullForObjectFields(props.value)
        } else {
            setIsNew(true);
            emptyCompetition = {
                ...emptyCompetition,
                institution: { id: auth.user?.institution?.id }
            }
            _competition = emptyCompetition
        }

        // console.log(_competition);
        setCompetition(_competition);
        // console.log(props.institutions);
        setInstitutions(props.institutions);

        getPositionCategories();
        getLegalBasis(CompetitionUtils.getLegalBasisByPositionCategory(_competition?.positionCategory));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    const getLegalBasis = async (_type) => {
        // console.log("getLegalBasis: ", _type)
        setLegalBasesList(await EnumService.getEntityLookupByFieldAndProperty1("NOM:COMPETITION_LEGAL_BASIS", _type));
    };

    const hideDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        formik.resetForm();
    };

    const saveCompetition = (data) => {
        competitionService.saveCompetitionJob(data)
            .then((savedCompetition) => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(savedCompetition, isNew, true, null);
                }
                hideDialog();
            })
            .catch((error) => {
                props.afterSave(null, isNew, false, error);
            });
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: competition?.id ? competition : emptyCompetition,
        validate: (data) => {
            let errors = {};

            // console.log("validate data: ", data);

            if (!data.name) {
                errors.name = translatedMessage('form.error.nameRo.required');
            }
            // if (!data.status) {
            //     errors.status = translatedMessage('form.error.competition.status.required');
            // }
            if (!data.institution.id) {
                errors.institution = translatedMessage('form.error.competition.institution.required');
            }
            if (!data.positionCategory) {
                errors.positionCategory = translatedMessage('form.error.competition.positionCategory.required');
            }

            if (!data.legalBasis) {
                errors.legalBasis = translatedMessage('form.error.competition.legalBasis.required');
            }

            if (!data.contactName || !data.contactName.trim()) {
                errors.contactName = translatedMessage("form.error.competition.contactName.required");
            }

            if (!data.contactPosition || !data.contactPosition.trim()) {
                errors.contactPosition = translatedMessage("form.error.competition.contactPosition.required");
            }

            if (!data.contactPhone || !data.contactPhone.trim()) {
                errors.contactPhone = translatedMessage("form.error.competition.contactPhone.required");
            }

            if (!data.contactEmail || !data.contactEmail.trim()) {
                errors.contactEmail = translatedMessage("form.error.competition.contactEmail.required");
            }
            else if (!/^[A-Z0-9._%+-]+@[A-Z0-9._-]+\.[A-Z]{2,100}$/i.test(data.contactEmail)) {
                errors.contactEmail = translatedMessage("form.error.competition.contactEmail.invalid");
            }
            if (!data.responsibleName || !data.responsibleName.trim()) {
                errors.responsibleName = translatedMessage("form.error.competition.responsibleName.required");
            }

            if (!data.responsiblePosition || !data.responsiblePosition.trim()) {
                errors.responsiblePosition = translatedMessage("form.error.competition.responsiblePosition.required");
            }

            if (!data.responsiblePhone || !data.responsiblePhone.trim()) {
                errors.responsiblePhone = translatedMessage("form.error.competition.responsiblePhone.required");
            }

            if (!data.responsibleEmail || !data.responsibleEmail.trim()) {
                errors.responsibleEmail = translatedMessage("form.error.competition.responsibleEmail.required");
            }
            else if (!/^[A-Z0-9._%+-]+@[A-Z0-9._-]+\.[A-Z]{2,100}$/i.test(data.responsibleEmail)) {
                errors.responsibleEmail = translatedMessage("form.error.competition.responsibleEmail.invalid");
            }

            if (!data.managerName || !data.managerName.trim()) {
                errors.managerName = translatedMessage("form.error.competition.managerName.required");
            }

            if (!data.managerPosition || !data.managerPosition.trim()) {
                errors.managerPosition = translatedMessage("form.error.competition.managerPosition.required");
            }

            if (data.institutionRegistrationDate && data.institutionRegistrationDate > new Date()) {
                errors.institutionRegistrationDate = translatedMessage("form.error.competition.institutionRegistrationDate.tooLate");
            }

            return errors;
        },
        onSubmit: (data) => {
            let _data = { ...data };
            _data.legalBasis = { ...data.legalBasis, id: data.legalBasis.value };
            // console.log("competition saved: ", _data);
            saveCompetition(data);
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const formDialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-save" className="p-button-text" form="field-form" type="submit" />
        </>
    );

    const handleDropdownChange = (data) => {
        // console.log("handleDropdownChange: ", data)
        let _targetField = data?.target?.id
        formik.setFieldValue(_targetField, data.value);
        if (_targetField === "positionCategory") {
            formik.setFieldValue("legalBasis", null);
            getLegalBasis(CompetitionUtils.getLegalBasisByPositionCategory(data.value));
        }
    }

    const dropdownTemplate = (option) => {
        return option.label;
    }

    const searchListItems = (event, list) => {
        let query = event.query;
        let _filteredItems = [];

        for (let i = 0; i < list.length; i++) {
            let item = list[i];
            if (item.label.toLowerCase().indexOf(query.toLowerCase()) >= 0) {
                _filteredItems.push(item);
            }
        }

        setFilteredEntityLookups(_filteredItems);
    }

    const handleListChange = async (data, field) => {
        //console.log("handleListChange", data, field);
        formik.setFieldValue(field, data.value)
    }

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={props?.visible}
                header={translatedMessage('competition.details')}
                modal
                className="pcn-dialog p-fluid"
                footer={formDialogFooter}
                onHide={hideDialog}
            >
                <form id="field-form" onSubmit={formik.handleSubmit}>
                    <div className='grid'>
                        <div className='col-12'>
                            <div className="p-inputgroup">
                                <span className="p-float-label">
                                    <InputText
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        autoFocus
                                        className={classNames({ 'p-invalid': isFormFieldValid('name') })}
                                        maxLength={255}
                                        tooltip={translatedMessage("field.character.maxLength", "255")}
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                    <label htmlFor="name" className={classNames({ 'p-error': isFormFieldValid('name') })}>
                                        {translatedMessage('generic.nameRo')} *
                                    </label>
                                </span>
                            </div>
                            {getFormErrorMessage('name')}
                        </div>

                        <div className='col-12 lg:col-6'>
                            <div className="p-inputgroup">
                                <span className="p-float-label">
                                    <Dropdown
                                        id="institution.id"
                                        name="institution.id"
                                        value={formik.values.institution?.id}
                                        onChange={handleDropdownChange}
                                        options={institutions}
                                        itemTemplate={dropdownTemplate}
                                        scrollHeight="400px"
                                        className={classNames({ 'p-invalid': isFormFieldValid('institution.id') })}
                                    />
                                    <label htmlFor="institution.id" className={classNames({ 'p-error': isFormFieldValid('institution.id') })}>
                                        {translatedMessage('competition.institution')} *
                                    </label>
                                </span>
                            </div>
                            {getFormErrorMessage('institution.id')}
                        </div>

                        <div className='col-12 lg:col-6'>
                            <div className="p-inputgroup">
                                <span className="p-float-label">
                                    <Dropdown
                                        id="positionCategory"
                                        name="positionCategory"
                                        value={formik.values.positionCategory}
                                        onChange={handleDropdownChange}
                                        options={positionCategories}
                                        itemTemplate={dropdownTemplate}
                                        scrollHeight="400px"
                                        className={classNames({ 'p-invalid': isFormFieldValid('positionCategory') })}
                                    />
                                    <label htmlFor="positionCategory" className={classNames({ 'p-error': isFormFieldValid('positionCategory') })}>
                                        {translatedMessage('competition.positionCategory')} *
                                    </label>
                                </span>
                            </div>
                            {getFormErrorMessage('positionCategory')}
                        </div>

                        <div className='col-12'>
                            <div className="p-inputgroup">
                                <span className="p-float-label">
                                    <AutoComplete
                                        dropdown
                                        id="legalBasis"
                                        name="legalBasis"
                                        value={formik.values?.legalBasis}
                                        suggestions={filteredEntityLookups}
                                        completeMethod={(e) => searchListItems(e, legalBasisList)}
                                        virtualScrollerOptions={{ itemSize: 38 }}
                                        field="label"
                                        onChange={(d) => handleListChange(d, "legalBasis")}
                                        emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                        className={classNames({ 'p-invalid': isFormFieldValid('legalBasis') })}
                                        panelClassName='pcn-min-200'
                                    />
                                    <label htmlFor="legalBasis" className={classNames({ 'p-error': isFormFieldValid('legalBasis') })}>
                                        {translatedMessage('competition.legalBasis')} *
                                    </label>
                                </span>
                            </div>
                            {getFormErrorMessage('legalBasis')}
                        </div>

                        <div className='col-12'>
                            <div className="flex align-items-center">
                                <Checkbox inputId="hasResponsibility" name="hasResponsibility" checked={formik.values.hasResponsibility} onChange={formik.handleChange} disabled />
                                <label htmlFor="hasResponsibility" className="ml-2">{translatedMessage("competition.hasResponsibility")}</label>
                            </div>
                        </div>

                        <div className='col-12'>
                            <div className="flex align-items-center">
                                <Checkbox inputId="anfpRequest" name="anfpRequest" checked={formik.values.anfpRequest} onChange={formik.handleChange} />
                                <label htmlFor="anfpRequest" className="ml-2">{translatedMessage("competition.anfpRequest")}</label>
                            </div>
                        </div>

                        <div className='col-12'>
                            <div className="mt-2 mb-2 font-bold">{translatedMessage("competition.contactPerson")}</div>
                            <div className='grid'>
                                <div className='col-12 lg:col-3'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText type="text" name="contactName" id="contactName"
                                                value={formik.values.contactName} onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('contactName') })} />
                                            <label htmlFor="contactName" className={classNames({ 'p-error': isFormFieldValid('contactName') })}>
                                                {translatedMessage("competition.contactName")} *
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('contactName')}
                                </div>

                                <div className='col-12 lg:col-3'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText type="text" name="contactPosition" id="contactPosition"
                                                value={formik.values.contactPosition} onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('contactPosition') })} />
                                            <label htmlFor="contactPosition" className={classNames({ 'p-error': isFormFieldValid('contactPosition') })}>
                                                {translatedMessage("competition.contactPosition")} *
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('contactPosition')}
                                </div>

                                <div className='col-12 lg:col-3'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText type="text" name="contactPhone" id="contactPhone"
                                                value={formik.values.contactPhone} onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('contactPhone') })} keyfilter="int" maxLength={10}
                                                tooltip={translatedMessage("profile.phone.format")} tooltipOptions={{ position: 'top' }} />
                                            <label htmlFor="contactPhone" className={classNames({ 'p-error': isFormFieldValid('contactPhone') })}>
                                                {translatedMessage("competition.contactPhone")} *
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('contactPhone')}
                                </div>

                                <div className='col-12 lg:col-3'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText type="text" name="contactEmail" id="contactEmail"
                                                value={formik.values.contactEmail} onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('contactEmail') })} />
                                            <label htmlFor="contactEmail" className={classNames({ 'p-error': isFormFieldValid('contactEmail') })}>
                                                {translatedMessage("competition.contactEmail")} *
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('contactEmail')}
                                </div>
                            </div>
                        </div>

                        <div className='col-12'>
                            <div className="mt-2 mb-2 font-bold">{translatedMessage("competition.responsiblePerson")}</div>
                            <div className='grid'>
                                <div className='col-12 lg:col-3'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText type="text" name="responsibleName" id="responsibleName"
                                                value={formik.values.responsibleName} onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('responsibleName') })} />
                                            <label htmlFor="responsibleName" className={classNames({ 'p-error': isFormFieldValid('responsibleName') })}>
                                                {translatedMessage("competition.responsibleName")} *
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('responsibleName')}
                                </div>

                                <div className='col-12 lg:col-3'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText type="text" name="responsiblePosition" id="responsiblePosition"
                                                value={formik.values.responsiblePosition} onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('responsiblePosition') })} />
                                            <label htmlFor="responsiblePosition" className={classNames({ 'p-error': isFormFieldValid('responsiblePosition') })}>
                                                {translatedMessage("competition.responsiblePosition")} *
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('responsiblePosition')}
                                </div>

                                <div className='col-12 lg:col-3'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText type="text" name="responsiblePhone" id="responsiblePhone"
                                                value={formik.values.responsiblePhone} onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('responsiblePhone') })} keyfilter="int" maxLength={10}
                                                tooltip={translatedMessage("profile.phone.format")} tooltipOptions={{ position: 'top' }} />
                                            <label htmlFor="responsiblePhone" className={classNames({ 'p-error': isFormFieldValid('responsiblePhone') })}>
                                                {translatedMessage("competition.responsiblePhone")} *
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('responsiblePhone')}
                                </div>

                                <div className='col-12 lg:col-3'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText type="text" name="responsibleEmail" id="responsibleEmail"
                                                value={formik.values.responsibleEmail} onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('responsibleEmail') })} />
                                            <label htmlFor="responsibleEmail" className={classNames({ 'p-error': isFormFieldValid('responsibleEmail') })}>
                                                {translatedMessage("competition.responsibleEmail")} *
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('responsibleEmail')}
                                </div>
                            </div>
                        </div>

                        <div className='col-12'>
                            <div className="mt-2 mb-2 font-bold">{translatedMessage("competition.institution.manager.info")}</div>
                            <div className='grid'>
                                <div className='col-12 lg:col-6'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText type="text" name="managerName" id="managerName"
                                                value={formik.values.managerName} onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('managerName') })} />
                                            <label htmlFor="managerName " className={classNames({ 'p-error': isFormFieldValid('managerName') })}>
                                                {translatedMessage("competition.managerName")} *
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('managerName')}
                                </div>

                                <div className='col-12 lg:col-6'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText type="text" name="managerPosition" id="managerPosition"
                                                value={formik.values.managerPosition} onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('managerPosition') })} />
                                            <label htmlFor="managerPosition" className={classNames({ 'p-error': isFormFieldValid('managerPosition') })}>
                                                {translatedMessage("competition.managerPosition")} *
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('managerPosition')}
                                </div>
                            </div>
                        </div>

                        <div className='col-12'>
                            <div className="mt-2 mb-2 font-bold">{translatedMessage("competition.institutionRegistrationNumber")}</div>
                            <div className='grid'>
                                <div className='col-12 lg:col-6'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText
                                                type="text"
                                                id="institutionRegistrationNumber"
                                                name="institutionRegistrationNumber"
                                                value={formik.values.institutionRegistrationNumber}
                                                onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('institutionRegistrationNumber') })}
                                            />
                                            <label htmlFor="institutionRegistrationNumber" className={classNames({ 'p-error': isFormFieldValid('institutionRegistrationNumber') })}>
                                                {translatedMessage('generic.registrationNumber')}
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('institutionRegistrationNumber')}
                                </div>

                                <div className='col-12 lg:col-6'>
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <Calendar
                                                id="institutionRegistrationDate"
                                                name="institutionRegistrationDate"
                                                value={formik.values.institutionRegistrationDate}
                                                onChange={formik.handleChange}
                                                dateFormat="dd-mm-yy"
                                                className={classNames({ 'p-invalid': isFormFieldValid('institutionRegistrationDate') })}
                                            />
                                            <label htmlFor="institutionRegistrationDate" className={classNames({ 'p-error': isFormFieldValid('institutionRegistrationDate') })}>
                                                {translatedMessage("generic.registrationDate")}
                                            </label>
                                        </span>
                                    </div>
                                    {getFormErrorMessage('institutionRegistrationDate')}
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </Dialog >
        </>
    );
};

export default CompetitionJobEditDialog;
